// src/components/Navbar.js - Cinematic enhanced (sound + glow)
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useSound } from '../hooks/useSound';
import { useCinematic } from '../hooks/useCinematic';
import useViewport from '../hooks/useViewport';

const NAV = [
  { to:'/',             label:'DASH'    },
  { to:'/missions',     label:'MISSIONS'},
  { to:'/leaderboard',  label:'RANKS'   },
  { to:'/shop',         label:'SHOP'    },
  { to:'/achievements', label:'ACHIEV.' },
  { to:'/world',        label:'WORLD'   },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { onlineCount }  = useSocket();
  const loc = useLocation();
  const nav = useNavigate();
  const { playClick, playHover, playTransition } = useSound();
  const { spawnButtonSparks } = useCinematic();
  const { isMobile, isTablet } = useViewport();
  if (!user) return null;

  const xpPct = Math.min(((user.xp - (user.level-1)*500) / 500) * 100, 100);
  const active = to => to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to);

  const handleNavClick = (e, to) => {
    if (!active(to)) { playTransition(); spawnButtonSparks(e, '#00f5ff'); }
  };

  const handleLogout = (e) => {
    playClick(); spawnButtonSparks(e, '#ff4444');
    logout(); nav('/auth');
  };

  return (
    <nav style={{
      display:'flex',
      alignItems:'center',
      flexWrap:isTablet ? 'wrap' : 'nowrap',
      padding:isMobile ? '6px 10px' : '0 14px',
      minHeight:isTablet ? 'auto' : '52px',
      height:isTablet ? 'auto' : '52px',
      flexShrink:0,
      gap:isTablet ? '6px' : 0,
      background:'rgba(10,10,15,0.98)',
      borderBottom:'1px solid rgba(0,245,255,0.13)',
      zIndex:100,
      position:'relative',
      boxShadow:'0 2px 28px rgba(0,245,255,0.06)',
    }}>
      <style>{`
        .nav-item { transition: all 0.18s cubic-bezier(0.16,1,0.3,1) !important; }
        .nav-item:hover { transform: translateY(-1px) !important; }
        @keyframes navPulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
        .cin-logo-text {
          font-family: var(--font-game);
          font-size: ${isMobile ? 13 : 17}px;
          font-weight: 900;
          background: linear-gradient(135deg,#00f5ff,#7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: ${isMobile ? 2 : 3}px;
          filter: drop-shadow(0 0 7px rgba(0,245,255,0.45));
          display: inline-block;
        }
      `}</style>

      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'1px',
                    background:'linear-gradient(90deg,transparent,rgba(0,245,255,0.4) 30%,rgba(124,58,237,0.4) 70%,transparent)',
                    animation:'navPulse 3s ease-in-out infinite' }} />

      <Link to="/" style={{ textDecoration:'none', marginRight:isTablet ? '8px' : '14px', flexShrink:0 }}
        onClick={e => handleNavClick(e, '/')}>
        <span className="cin-logo-text">CODRION</span>
      </Link>

      <div style={{ display:'flex', gap:'2px', flex:isTablet ? '1 1 100%' : 1, overflowX:'auto', order:isTablet ? 3 : 'initial' }}>
        {NAV.map(l => (
          <Link key={l.to} to={l.to} style={{ textDecoration:'none', flexShrink:0 }}
            onClick={e => handleNavClick(e, l.to)}>
            <div className="nav-item" style={{
              padding:isMobile ? '4px 8px' : '5px 10px',
              borderRadius:'4px',
              cursor:'pointer',
              border:`1px solid ${active(l.to) ? 'rgba(0,245,255,0.38)' : 'transparent'}`,
              background: active(l.to) ? 'rgba(0,245,255,0.09)' : 'transparent',
              color: active(l.to) ? '#00f5ff' : 'rgba(255,255,255,0.45)',
              fontFamily:'var(--font-game)',
              fontSize:isMobile ? '12px' : '15px',
              fontWeight:700,
              letterSpacing:'0.5px',
              display:'flex',
              alignItems:'center',
              gap:'4px',
              boxShadow: active(l.to) ? '0 0 14px rgba(0,245,255,0.14)' : 'none',
            }}
              onMouseEnter={() => playHover()}
            >
              {l.label}
            </div>
          </Link>
        ))}
        {user.role === 'admin' && (
          <Link to="/admin" style={{ textDecoration:'none', flexShrink:0 }}
            onClick={e => handleNavClick(e, '/admin')}>
            <div className="nav-item" style={{
              padding:isMobile ? '4px 8px' : '5px 10px', borderRadius:'4px', cursor:'pointer',
              border:'1px solid rgba(255,68,68,0.2)', background:'rgba(255,68,68,0.06)',
              color:'#ff8888', fontFamily:'var(--font-game)', fontSize:isMobile ? '12px' : '15px', fontWeight:700,
              display:'flex', alignItems:'center', gap:'4px',
            }}>ADMIN</div>
          </Link>
        )}
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:isMobile ? '6px' : '10px', flexShrink:0, marginLeft:isTablet ? 'auto' : '8px', order:isTablet ? 2 : 'initial' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:isMobile ? '12px' : '15px', color:'#39ff14' }}>
          <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#39ff14',
                        animation:'pulse 2s infinite', boxShadow:'0 0 7px #39ff14' }} />
          {onlineCount}
        </div>
        <div style={{ fontFamily:'var(--font-game)', fontSize:isMobile ? '12px' : '15px', fontWeight:700, color:'#ffd700',
                      textShadow:'0 0 9px rgba(255,215,0,0.45)' }}>COINS {user.coins}</div>

        {!isMobile && (
          <div style={{ display:'flex', flexDirection:'column', gap:'2px', minWidth:'85px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'15px', color:'rgba(255,255,255,0.3)', fontFamily:'var(--font-game)' }}>
              <span>LVL {user.level}</span><span>{user.xp}XP</span>
            </div>
            <div className="xp-bar" style={{ height:'4px' }}>
              <div className="xp-fill" style={{ width:`${xpPct}%` }} />
            </div>
          </div>
        )}

        <Link to="/profile" style={{ textDecoration:'none' }}>
          <div style={{
            display:'flex', alignItems:'center', gap:'6px', padding:isMobile ? '3px 8px' : '4px 10px', borderRadius:'18px',
            background:'rgba(0,245,255,0.05)', border:'1px solid rgba(0,245,255,0.12)', cursor:'pointer',
            transition:'all 0.2s',
          }}
            onMouseEnter={e => { playHover(); e.currentTarget.style.boxShadow='0 0 16px rgba(0,245,255,0.18)'; e.currentTarget.style.borderColor='rgba(0,245,255,0.32)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.borderColor='rgba(0,245,255,0.12)'; }}
          >
            <span style={{ fontSize:isMobile ? '14px' : '17px' }}>{user.avatar || (user.username || 'U').slice(0, 1).toUpperCase()}</span>
            {!isMobile && (
              <span style={{ fontWeight:700, fontSize:'15px', color:'#fff', maxWidth:'70px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {user.username}
              </span>
            )}
          </div>
        </Link>

        <button onClick={handleLogout} style={{
          padding:isMobile ? '4px 8px' : '5px 10px', background:'rgba(255,68,68,0.06)', border:'1px solid rgba(255,68,68,0.18)',
          borderRadius:'4px', color:'rgba(255,100,100,0.7)', cursor:'pointer',
          fontFamily:'var(--font-game)', fontSize:isMobile ? '12px' : '15px', fontWeight:700, transition:'all 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background='rgba(255,68,68,0.15)'; e.currentTarget.style.color='#ff6464'; e.currentTarget.style.boxShadow='0 0 12px rgba(255,68,68,0.22)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='rgba(255,68,68,0.06)'; e.currentTarget.style.color='rgba(255,100,100,0.7)'; e.currentTarget.style.boxShadow=''; }}
        >OUT</button>
      </div>
    </nav>
  );
}
